import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import { EavEntityTypeFormDto, type EavEntityTypeFormInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-form.dto';
import { EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT } from '@zaoblako/shared/modules/eav/constants/eav-attribute-types-excluded-in-personal-account.constants';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { resolveEavAttributeShowInPersonalAccount } from '@zaoblako/shared/modules/eav/utils/resolve-eav-attribute-show-in-personal-account.util';
import { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BasisCatalogEavEntityTypeService } from '@zaoblako/web/core/services/basis/basis-catalog-eav-entity-type.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { BalanceReportIncomePercentModeNamePipe } from '@zaoblako/web/shared/pipes/balance-report-income-percent-mode-name.pipe';
import { EavComputedExpressionPipe } from '@zaoblako/web/shared/pipes/eav-computed-expression.pipe';
import { EavAttributeValueTypeNamePipe } from '@zaoblako/web/shared/pipes/eav-attribute-value-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import type { EavAttributeFormInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-form.dto';
import type { EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import type { EavEntityTypeFindOneQueryInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find-one-query.dto';

import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { MessagesModule } from 'primeng/messages';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TreeModule } from 'primeng/tree';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable } from 'rxjs';
import type { TreeNode } from 'primeng/api';

@Component({
  standalone: true,
  selector: 'crm-eav-entity-type-form',
  templateUrl: './eav-entity-type-form.component.html',
  styleUrls: ['./eav-entity-type-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    SpinnerComponent,
    JoinPipe,
    ButtonComponent,
    CardModule,
    MessagesModule,
    InputTextComponent,
    SelectButtonModule,
    TableComponent,
    SingleSelectComponent,
    MultiSelectComponent,
    OverlayPanelModule,
    TreeModule,
    OrderByPipe,
    EavComputedExpressionPipe,
    BalanceReportIncomePercentModeNamePipe,
    EavAttributeValueTypeNamePipe,
    CheckboxModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EavEntityTypeFormComponent
  extends BaseFormComponent<EavEntityTypeFormInterface>
  implements OnInit {
  @ViewChild('opExpression') public opExpression: OverlayPanel;

  @Input() public readonly index: number;
  public dto = EavEntityTypeFormDto;
  public type: EavEntityTypeFindInterface = <EavEntityTypeFindInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public attributeColumns: CrmTableColumn[] = [];

  public attributeStatuses: HelpdeskTaskStatusFindInterface[] = [];

  public attributeTypes: EavAttributeValueTypeEnum[] = Object.values(EavAttributeValueTypeEnum);

  public attributeErrors: string[] = [];

  public isHelpDeskModuleEnabled = false;

  public currentComputedAttribute: EavAttributeFindInterface | null = null;

  public computedAttributeTokensTree: TreeNode[] = [];

  public isShowOverlayPanel = false;

  public get EavAttributeValueTypeEnum() {
    return EavAttributeValueTypeEnum;
  }

  public constructor(
    private readonly basisCatalogEavEntityTypeService: BasisCatalogEavEntityTypeService,
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly confirmService: ConfirmDialogService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService
  ) {
    super();
    this.isHelpDeskModuleEnabled = this.globalService.isEnableModule('HELPDESK');
    this.attributeColumns = this.buildAttributeColumns();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.helpDeskTaskService.getStatuses().subscribe((statuses) => (this.attributeStatuses = statuses));

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    const queryParams: EavEntityTypeFindOneQueryInterface = {
      select: ['id', 'created', 'updated', 'deleted', 'name', 'attributes', 'tokens']
    };

    this.basisCatalogEavEntityTypeService.findById<EavEntityTypeFindInterface>(entityId, queryParams).subscribe({
      next: (type) => {
        this.type = type;
        this.computedAttributeTokensTree = this.buildComputedAttributeTokensTree(this.type.tokens);
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveEavEntityType(): void {
    this.attributeErrors = [];

    if (!this.type.attributes?.length) {
      this.attributeErrors.push('Список не может быть пустым');
      return;
    }

    this.type.attributes?.forEach((attribute, index) => {
      if (!attribute.name) {
        this.attributeErrors.push(`${index + 1} строчка: Название не указано`);
      }
      if (!attribute.type) {
        this.attributeErrors.push(`${index + 1} строчка: Тип поля не указан`);
      }
    });

    if (this.checkValidate(this.type).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisCatalogEavEntityTypeService
          .update<EavEntityTypeFormInterface, EavEntityTypeFindInterface>(this.type)
          .subscribe({
            next: (type) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон полей "${this.type.name}" обновлен!`);
              this.formStoreService.saveNext(type, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisCatalogEavEntityTypeService.create<EavEntityTypeFindInterface>(this.type).subscribe({
          next: (type) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон полей создан!');
            this.formStoreService.saveNext(type, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteEavEntityType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон полей "${this.type.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisCatalogEavEntityTypeService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Шаблон полей "${this.type.name}" удален!`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreEavEntityType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить шаблон полей "${this.type.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisCatalogEavEntityTypeService
          .restoreOne(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.type = { ...this.type, deleted: null };
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Шаблон полей "${this.type.name}" восстановлен!`);
              this.formStoreService.saveNext(this.type, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public toAttribute(attribute: any): EavAttributeFindInterface {
    return attribute as EavAttributeFindInterface;
  }

  public addAttribute(): void {
    if (!Array.isArray(this.type.attributes)) {
      this.type.attributes = [];
    }

    this.type.attributes = this.type.attributes.concat(<EavAttributeFindInterface>{
      showInPersonalAccount: false
    });
    this.loadTokensForAttributes();
  }

  public deleteAttribute(attribute: EavAttributeFindInterface): void {
    this.type.attributes = this.type.attributes.filter((a) => a !== attribute);
    if (this.currentComputedAttribute === attribute) {
      this.currentComputedAttribute = null;
    }
    this.loadTokensForAttributes();
  }

  public readonly onAttributeTypeChange = (
    attribute: EavAttributeFindInterface,
    type: EavAttributeValueTypeEnum | null
  ): void => {
    if (!attribute) {
      return;
    }

    if (attribute.type === EavAttributeValueTypeEnum.COMPUTED && type !== EavAttributeValueTypeEnum.COMPUTED) {
      attribute.expression = null;
    }

    attribute.type = type;

    if (!this.isShowInPersonalAccountEditable(type)) {
      attribute.showInPersonalAccount = false;
    }

    this.loadTokensForAttributes();
  };

  public readonly isShowInPersonalAccountEditable = (type: EavAttributeValueTypeEnum | null | undefined): boolean => {
    if (!type) {
      return false;
    }

    return !EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT.includes(type);
  };

  public readonly openComputedAttributeTokens = (
    event: MouseEvent,
    attribute: EavAttributeFindInterface
  ): void => {
    this.currentComputedAttribute = attribute;
    this.opExpression.toggle(event);
  };

  public readonly selectComputedAttributeToken = (tokenNode: TreeNode): void => {
    if (!this.currentComputedAttribute || !tokenNode?.key) {
      return;
    }

    const currentExpression = this.currentComputedAttribute.expression || '';
    this.currentComputedAttribute.expression = `${currentExpression}${tokenNode.key}`;
  };

  public readonly keydownEscInExpressionInput = (event: KeyboardEvent): void => {
    if (this.isShowOverlayPanel) {
      this.opExpression.hide();
      this.currentComputedAttribute = null;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  private readonly buildAttributeColumns = (): CrmTableColumn[] => {
    const columns: CrmTableColumn[] = [
      {
        label: 'Название',
        class: 'eav-attr-name-col',
        style: { width: '14rem', minWidth: '14rem' }
      },
      { label: 'Тип поля', class: 'min-w-10rem' }
    ];

    if (this.isHelpDeskModuleEnabled) {
      columns.push({
        label: 'Обязательно в статусах',
        class: 'min-w-12rem',
        additionalProperty: 'только для HelpDesk'
      });
    }

    columns.push(
      { label: 'Отражать в ЛК Клиента', class: 'min-w-10rem white-space-nowrap' },
      { class: 'w-2' },
      { class: 'w-1' }
    );

    return columns;
  };

  private readonly buildComputedAttributeTokensTree = (tokens: EavEntityTypeFindInterface['tokens']): TreeNode[] => {
    if (!tokens?.length) {
      return [];
    }

    return tokens.map((token) => ({
      label: token.displayName,
      key: token.systemName,
      selectable: true,
      expanded: true,
      children: []
    }));
  };

  private readonly loadTokensForAttributes = (): void => {
    const rawAttributes = this.type.attributes || [];

    const attributesForTokens: EavAttributeFormInterface[] = rawAttributes
      .filter((attribute) => attribute?.type === EavAttributeValueTypeEnum.ROOM || attribute?.type === EavAttributeValueTypeEnum.NUMBER)
      .map((attribute) => ({
        id: attribute.id,
        name: attribute.name || '',
        type: attribute.type,
        multipleSelect: attribute.multipleSelect,
        expression: attribute.expression,
        requiredOnHdTaskStatuses: attribute.requiredOnHdTaskStatuses,
        showInPersonalAccount: resolveEavAttributeShowInPersonalAccount(
          attribute.type,
          attribute.showInPersonalAccount
        )
      }));

    if (!attributesForTokens.length) {
      this.computedAttributeTokensTree = [];
      return;
    }

    this.basisCatalogEavEntityTypeService.getTokensForAttributes(attributesForTokens).subscribe((tokens) => {
      this.computedAttributeTokensTree = this.buildComputedAttributeTokensTree(tokens);
      this.cdRef.markForCheck();
    });
  };
}
